import useTestId from '@ori/testid-generator';
import { useKeenSlider } from 'keen-slider/react';
import debounce from 'lodash.debounce';
import get from 'lodash.get';
import React, { useCallback, useEffect, Children, useState, useMemo } from 'react';
import { v4 } from 'uuid';
import { CAROUSEL, CAROUSEL_WRAPPER } from '../../common';
import { ImagePlaceholder } from '../ImagePlaceholder';
import { NavigationArrows } from './NavigationArrows';
import { Pagination } from './Pagination';
import { SLIDER_CLASSNAME, SLIDE_CLASSNAME, WAIT_TIME } from './constants';
import { useAutoplayProps, useNavigationProps, usePaginationProps, useSliderProps } from './hooks';
import { usePlugins } from './plugins';
import { CarouselInner, CarouselRoot } from './styles';
import { disableDragEventPropagation, getCurrentSlideIndex, isCarouselAtTheEnd, removeDisableDragEventPropagation, shouldShowNavigationArrows } from './utils';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const Carousel = ({
  autoplay,
  className,
  children,
  id,
  showPlaceholderIfEmpty,
  navigation,
  pagination,
  propagateDragEvent,
  testId,
  slider,
  width,
  renderPaginationStep
}) => {
  const autoplayProps = useAutoplayProps(autoplay);
  const navigationProps = useNavigationProps(navigation);
  const paginationProps = usePaginationProps(pagination);
  const selectorId = useMemo(() => id ?? `x${v4()}`, [id]);
  const sliderProps = useSliderProps({
    selector: `#${selectorId} > .${SLIDE_CLASSNAME}`,
    ...slider
  });
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesCount, setSlidesCount] = useState(Children.count(children));
  const [lastSlideVisible, setLastSlideVisible] = useState(false);
  const [isTouchActive, setIsTouchActive] = useState(false);
  const {
    getTestId
  } = useTestId();
  const updateLastSlideVisible = useCallback(progress => {
    setLastSlideVisible(isCarouselAtTheEnd(progress));
  }, []);
  const options = useMemo(() => {
    const updateLastSlideVisibleDebounce = debounce(updateLastSlideVisible, WAIT_TIME);
    return {
      ...sliderProps,
      created: instance => {
        setSlidesCount(get(instance, 'track.details.slides.length', Children.count(children)));
        disableDragEventPropagation({
          instance,
          propagateDragEvent
        });
        sliderProps.created?.(instance);
      },
      destroyed: instance => {
        removeDisableDragEventPropagation({
          instance,
          propagateDragEvent
        });
        sliderProps.destroyed?.(instance);
      },
      slideChanged: instance => {
        setCurrentSlide(get(instance, 'track.details.rel', 0));
        sliderProps.slideChanged?.(instance);
      },
      detailsChanged: instance => {
        updateLastSlideVisibleDebounce(get(instance, 'track.details.progress', 0));
        sliderProps.detailsChanged?.(instance);
      }
    };
  }, [children, propagateDragEvent, sliderProps, updateLastSlideVisible]);
  const [sliderRef, instanceRef] = useKeenSlider(options, usePlugins({
    autoplay: autoplayProps,
    children,
    className,
    navigation: navigationProps,
    pagination: paginationProps,
    propagateDragEvent,
    slider: sliderProps,
    testId,
    width
  }, setIsTouchActive));
  useEffect(() => {
    const childrenCount = Children.count(children);
    if (instanceRef.current) {
      instanceRef.current.update(options, getCurrentSlideIndex({
        currentSlideIndex: get(instanceRef, 'current.track.details.rel', 0),
        slidesCount: childrenCount
      }));
      setSlidesCount(get(instanceRef, 'current.track.details.slides.length', childrenCount));
    } else {
      setSlidesCount(childrenCount);
    }
  }, [children, instanceRef, options]);
  if (slidesCount < 1) {
    if (!showPlaceholderIfEmpty) {
      return null;
    }
    return /*#__PURE__*/_jsx(CarouselRoot, {
      "data-testid": getTestId(CAROUSEL_WRAPPER),
      width: width,
      children: /*#__PURE__*/_jsx(ImagePlaceholder, {
        isSquare: true
      })
    });
  }
  return /*#__PURE__*/_jsxs(CarouselRoot, {
    className: className,
    "data-testid": testId ?? getTestId(CAROUSEL_WRAPPER),
    isTouchActive: isTouchActive,
    navigation: navigationProps,
    width: width,
    children: [/*#__PURE__*/_jsx(CarouselInner, {
      ref: sliderRef,
      id: selectorId,
      className: SLIDER_CLASSNAME,
      "data-testid": getTestId(CAROUSEL),
      children: children
    }), paginationProps.show ? /*#__PURE__*/_jsx(Pagination, {
      autoplay: autoplayProps,
      slidesCount: slidesCount,
      currentSlide: currentSlide,
      instanceRef: instanceRef,
      pagination: paginationProps,
      renderPaginationStep: renderPaginationStep
    }) : null, shouldShowNavigationArrows({
      navigation: navigationProps,
      slider: sliderProps,
      slidesCount
    }) ? /*#__PURE__*/_jsx(NavigationArrows, {
      instanceRef: instanceRef,
      lastSlideVisible: lastSlideVisible,
      navigation: navigationProps,
      slider: sliderProps,
      slidesCount: slidesCount
    }) : null]
  });
};
//# sourceMappingURL=Carousel.js.map